import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

function fmt(n) { return Math.round(n || 0).toLocaleString(); }
function parseNum(str) { return parseInt(String(str).replace(/,/g, '')) || 0; }

function calcIncomeTax(income) {
  if (income <= 0) return 0;
  const brackets = [
    [14000000, 0.06, 0],
    [50000000, 0.15, 1260000],
    [88000000, 0.24, 5760000],
    [150000000, 0.35, 15440000],
    [300000000, 0.38, 19940000],
    [500000000, 0.40, 25940000],
    [1000000000, 0.42, 35940000],
    [Infinity, 0.45, 65940000],
  ];
  for (const [limit, rate, deduction] of brackets) {
    if (income <= limit) return Math.max(0, income * rate - deduction);
  }
}

const NOW = new Date();

export default function Finance() {
  const [period, setPeriod] = useState('month');
  const [customYear, setCustomYear] = useState(NOW.getFullYear());
  const [customMonth, setCustomMonth] = useState(NOW.getMonth() + 1);
  const [data, setData] = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [annualData, setAnnualData] = useState(null);
  const [manualData, setManualData] = useState(null);
  const [annualYear, setAnnualYear] = useState(NOW.getFullYear());

  const [form, setForm] = useState({ ad_cost: '', etc_cost: '', extra_amount: '', memo: '', ad_smartstore: '', ad_11st: '', ad_gmarket: '' });
  const [showMonthlyModal, setShowMonthlyModal] = useState(false);
  const [showAnnualModal, setShowAnnualModal] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [downloading, setDownloading] = useState(false);

  const openMonthlyModal = async () => {
    try {
      const r = await api.get('/finance/available-months');
      setAvailableMonths(r.data);
      setSelectedMonths([]);
      setShowMonthlyModal(true);
    } catch(e) { alert('데이터 조회 실패'); }
  };

  const openAnnualModal = async () => {
    try {
      const r = await api.get('/finance/available-years');
      setAvailableYears(r.data);
      setSelectedYear(r.data[0] || '');
      setShowAnnualModal(true);
    } catch(e) { alert('데이터 조회 실패'); }
  };

  const downloadMonthly = async () => {
    if (selectedMonths.length === 0) return alert('월을 선택해주세요');
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/finance/download/monthly', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ months: selectedMonths })
      });
      const blob = await res.blob();
      const monthLabels = selectedMonths.sort().map(m => m.split('-')[1]+'월');
      const year = selectedMonths[0].split('-')[0];
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `진하유통_재무_${year}년${monthLabels.join(',')}.xlsx`;
      a.click();
      setShowMonthlyModal(false);
    } catch(e) { alert('다운로드 실패'); }
    finally { setDownloading(false); }
  };

  const downloadAnnual = async () => {
    if (!selectedYear) return alert('연도를 선택해주세요');
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/finance/download/annual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ year: selectedYear })
      });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `진하유통_재무_${selectedYear}년.xlsx`;
      a.click();
      setShowAnnualModal(false);
    } catch(e) { alert('다운로드 실패'); }
    finally { setDownloading(false); }
  };
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const getYearMonth = useCallback(() => {
    if (period === 'month') return `${NOW.getFullYear()}-${String(NOW.getMonth()+1).padStart(2,'0')}`;
    if (period === 'lastmonth') {
      const d = new Date(NOW.getFullYear(), NOW.getMonth()-1, 1);
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    }
    return `${customYear}-${String(customMonth).padStart(2,'0')}`;
  }, [period, customYear, customMonth]);
  const getPrevYearMonth = useCallback(() => {
    if (period === 'month') {
      const d = new Date(NOW.getFullYear(), NOW.getMonth()-1, 1);
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    }
    if (period === 'lastmonth') {
      const d = new Date(NOW.getFullYear(), NOW.getMonth()-2, 1);
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    }
    const pm = customMonth - 1;
    if (pm === 0) return `${customYear-1}-12`;
    return `${customYear}-${String(pm).padStart(2,'0')}`;
  }, [period, customYear, customMonth]);

  const fetchData = useCallback(async (resetForm = false) => {
    const ym = getYearMonth();
    try {
      const res = await api.get(`/finance/${ym}`);
      setData(res.data);
      if (resetForm) {
        setForm({ ad_cost: res.data.ad_cost || '', etc_cost: res.data.etc_cost || '', extra_amount: res.data.extra_amount || '', memo: res.data.memo || '', ad_smartstore: res.data.ad_smartstore || '', ad_11st: res.data.ad_11st || '', ad_gmarket: res.data.ad_gmarket || '' });
      }
    } catch (err) { console.error(err); }
  }, [getYearMonth]);

  const fetchPrevData = useCallback(async () => {
    const pym = getPrevYearMonth();
    try {
      const res = await api.get(`/finance/${pym}`);
      setPrevData(res.data);
    } catch (err) { setPrevData(null); }
  }, [getPrevYearMonth]);
  const fetchManual = useCallback(async () => {
    try {
      const res = await api.get(`/finance/manual/${annualYear}`);
      setManualData(res.data);
    } catch (err) { console.error(err); }
  }, [annualYear]);

  const fetchAnnual = useCallback(async () => {
    try {
      const res = await api.get(`/finance/annual/${annualYear}`);
      setAnnualData(res.data);
    } catch (err) { console.error(err); }
  }, [annualYear]);

  useEffect(() => { fetchData(true); }, [fetchData]);
  useEffect(() => { fetchPrevData(); }, [fetchPrevData]);
  useEffect(() => { fetchAnnual(); }, [fetchAnnual]);


  useEffect(() => { fetchManual(); }, [fetchManual]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const ym = getYearMonth();
      const _extraAmount = parseNum(String(form.extra_amount)) || 0;
      const _A = data.total_sales + _extraAmount;
      const _B = data.total_settlement;
      const _adSmartstore = parseNum(String(form.ad_smartstore)) || 0;
      const _ad11st = parseNum(String(form.ad_11st)) || 0;
      const _adGmarket = parseNum(String(form.ad_gmarket)) || 0;
      const _adCost = (_adSmartstore + _ad11st + _adGmarket) || parseNum(String(form.ad_cost)) || data.ad_cost;
      const _etcCost = parseNum(String(form.etc_cost)) || data.etc_cost;
      const _C = data.total_purchase;
      const _Cplus = _etcCost;
      const _D = _B - _C - _Cplus;
      const _vatSale = Math.round(_A * 0.1);
      const _vatBuy = Math.round((_C + _Cplus) * 0.1);
      const _E = _vatSale - _vatBuy;
      const _F = _adCost;
      const _G = _D - _E - _F;
      const _H = Math.max(0, _G) * effRate;
      const _I = _G - _H;
      const _opMarginRate = _A > 0 ? (_D / _A * 100) : 0;
      const _netMarginRate = _A > 0 ? (_I / _A * 100) : 0;
      const _roas = _F > 0 ? Math.round(_A / _F * 100) : 0;
      const _roi = _F > 0 ? Math.round(_I / _F * 100) : 0;
      const _bep = _F > 0 ? Math.round((_F + _E + _H) / (1 - (_C + _Cplus) / _B)) : 0;
      const _stab = _bep > 0 ? (_A / _bep) : 0;
      const _adCostRatio = _A > 0 ? (_F / _A * 100) : 0;
      const _taxBurdenRate = _G > 0 ? ((_E + _H) / _G * 100) : 0;

      await api.post(`/finance/${ym}`, {
        ad_cost: _adCost,
        ad_smartstore: _adSmartstore,
        ad_11st: _ad11st,
        ad_gmarket: _adGmarket,
        etc_cost: _etcCost,
        extra_amount: _extraAmount,
        memo: form.memo,
        total_sales: _A,
        total_settlement: _B,
        total_purchase: _C,
        d_operating_profit: Math.round(_D),
        e_vat: Math.round(_E),
        e_vat_sale: _vatSale,
        e_vat_buy: _vatBuy,
        f_ad_cost: _F,
        g_pretax: Math.round(_G),
        h_income_tax: Math.round(_H),
        i_net_income: Math.round(_I),
        op_margin_rate: _opMarginRate,
        net_margin_rate: _netMarginRate,
        roas: _roas,
        roi: _roi,
        bep: _bep,
        stability: _stab,
        ad_cost_ratio: _adCostRatio,
        tax_burden_rate: _taxBurdenRate,
      });
      setSaveMsg('저장됐습니다');
      setTimeout(() => setSaveMsg(''), 3000);
      fetchData(false);
      fetchAnnual();
    } catch (err) { alert('저장 실패'); }
    finally { setSaving(false); }
  };

  const handleMemoSave = async () => {
    setSaving(true);
    try {
      const ym = getYearMonth();
      await api.post(`/finance/${ym}`, {
        ad_cost: parseNum(String(form.ad_cost)) || data.ad_cost,
        etc_cost: parseNum(String(form.etc_cost)) || data.etc_cost,
        memo: form.memo,
        ad_smartstore: parseNum(String(form.ad_smartstore)) || data.ad_smartstore || 0,
        ad_11st: parseNum(String(form.ad_11st)) || data.ad_11st || 0,
        ad_gmarket: parseNum(String(form.ad_gmarket)) || data.ad_gmarket || 0,
      });
      setSaveMsg('메모가 저장됐습니다');
      setTimeout(() => setSaveMsg(''), 3000);
      fetchData(false);
    } catch (err) { alert('저장 실패'); }
    finally { setSaving(false); }
  };

  if (!data) return <div style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>로딩 중...</div>;

  const extraAmount = parseNum(String(form.extra_amount)) || 0;
  const A = data.total_sales + extraAmount;
  const B = data.total_settlement;
  const adCost = ((parseNum(String(form.ad_smartstore))||0) + (parseNum(String(form.ad_11st))||0) + (parseNum(String(form.ad_gmarket))||0)) || parseNum(String(form.ad_cost)) || data.ad_cost;
  const etcCost = parseNum(String(form.etc_cost)) || data.etc_cost;
  const C = data.total_purchase;
  const Cplus = etcCost;
  const D = B - C - Cplus;
  const vatSale = Math.round(A * 0.1);
  const vatBuy = Math.round((C + Cplus) * 0.1);
  const E = vatSale - vatBuy;
  // 구입부가세 = (월매입액 + 기타비용) × 10%
  const F = adCost;
  const G = D - E - F;

  // 연간 종합에서 실효세율 계산
  const manualPretax = manualData ? (() => {
    const mp = manualData.manual_settlement - manualData.manual_purchase - (manualData.etc_cost||0) - (manualData.ad_cost||0);
    return mp;
  })() : 0;
  const annualPretax = (annualData ? annualData.months.reduce((s, m) => s + (m.pretax || 0), 0) : 0) + manualPretax;
  const annIncomeTax = calcIncomeTax(annualPretax);
  const annLocalTax = annIncomeTax * 0.1;
  const annTotalTax = annIncomeTax + annLocalTax;
  const effRate = annualPretax > 0 ? annTotalTax / annualPretax : 0;
  const H = Math.max(0, G) * effRate;
  const I = G - H;

  const prevD = (prevData && annualData) ? (() => {
    const pB = prevData.total_settlement;
    const pC = prevData.total_purchase;
    const pCplus = prevData.etc_cost || 0;
    return pB - pC - pCplus;
  })() : null;
  const prevE = (prevData && annualData) ? (() => {
    const pA = prevData.total_sales + (parseFloat(prevData.extra_amount) || 0);
    const pC = prevData.total_purchase;
    const pCplus = prevData.etc_cost || 0;
    return Math.round(pA * 0.1) - Math.round((pC + pCplus) * 0.1);
  })() : null;
  const prevF = (prevData && annualData) ? (prevData.ad_cost || 0) : null;
  const prevG = (prevD !== null && prevE !== null && prevF !== null) ? prevD - prevE - prevF : null;
  const prevH = (prevG !== null && prevG > 0) ? prevG * effRate : (prevG !== null ? 0 : null);
  const prevI = (prevG !== null && prevH !== null) ? prevG - prevH : null;
  const opMarginRate = A > 0 ? (D / A * 100) : 0;
  const netMarginRate = A > 0 ? (I / A * 100) : 0;
  const roas = F > 0 ? Math.round(A / F * 100) : 0;
  const roi = F > 0 ? Math.round(I / F * 100) : 0;

  const ym = getYearMonth();
  const [ymYear, ymMonth] = ym.split('-');

  const inp = { fontSize: '12px', padding: '5px 8px', borderRadius: '6px', border: '0.5px solid #ddd', width: '130px', textAlign: 'right', boxSizing: 'border-box' };

  return (
    <div style={{ minWidth: '1000px', maxWidth: '1000px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h1 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>재무표</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {saveMsg && <span style={{ fontSize: '12px', color: '#1D9E75' }}>{saveMsg}</span>}
          <button onClick={handleSave} disabled={saving} style={{ fontSize: '12px', padding: '7px 20px', borderRadius: '8px', border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer', fontWeight: '500' }}>
            {saving ? '저장 중...' : `${ymYear}년 ${ymMonth}월 재무표 저장`}
          </button>
        </div>
      </div>

      {/* 기간 탭 */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', alignItems: 'center' }}>
        {[['month','이번달'],['lastmonth','지난달'],['custom','직접입력']].map(([k,v]) => (
          <button key={k} onClick={() => setPeriod(k)} style={{ fontSize: '12px', padding: '4px 14px', borderRadius: '20px', border: `0.5px solid ${period===k?'#1D9E75':'#e8e8e8'}`, background: period===k?'#E1F5EE':'#fff', color: period===k?'#0F6E56':'#888', cursor: 'pointer' }}>{v}</button>
        ))}
        {period === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <select value={customYear} onChange={e => setCustomYear(parseInt(e.target.value))} style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '8px', border: '0.5px solid #e8e8e8' }}>
              {[2025,2026,2027].map(y => <option key={y} value={y}>{y}년</option>)}
            </select>
            <select value={customMonth} onChange={e => setCustomMonth(parseInt(e.target.value))} style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '8px', border: '0.5px solid #e8e8e8' }}>
              {Array.from({length:12},(_,i)=>i+1).map(m => <option key={m} value={m}>{m}월</option>)}
            </select>
          </div>
        )}
      </div>

      {/* 입력 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        <div style={{ padding: '10px 14px', background: '#f9f9f9', borderRadius: '8px' }}>
          <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>월 광고비 <span style={{ color: '#aaa' }}>(합계: {fmt((parseNum(String(form.ad_smartstore))||0)+(parseNum(String(form.ad_11st))||0)+(parseNum(String(form.ad_gmarket))||0))}원)</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', color: '#666', width: '90px' }}>스마트스토어</span>
              <input style={inp} type="text" value={form.ad_smartstore} onChange={e => setForm(p=>({...p, ad_smartstore: e.target.value.replace(/[^0-9]/g,'')}))} placeholder="0" />
              <span style={{ fontSize: '11px', color: '#aaa' }}>원</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', color: '#666', width: '90px' }}>11번가</span>
              <input style={inp} type="text" value={form.ad_11st} onChange={e => setForm(p=>({...p, ad_11st: e.target.value.replace(/[^0-9]/g,'')}))} placeholder="0" />
              <span style={{ fontSize: '11px', color: '#aaa' }}>원</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', color: '#666', width: '90px' }}>지마켓</span>
              <input style={inp} type="text" value={form.ad_gmarket} onChange={e => setForm(p=>({...p, ad_gmarket: e.target.value.replace(/[^0-9]/g,'')}))} placeholder="0" />
              <span style={{ fontSize: '11px', color: '#aaa' }}>원</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: '#f9f9f9', borderRadius: '8px' }}>
          <span style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>기타비용</span>
          <input style={inp} type="text" value={form.etc_cost} onChange={e => setForm(p=>({...p, etc_cost: e.target.value.replace(/[^0-9]/g,'')}))} placeholder="0" />
          <span style={{ fontSize: '11px', color: '#aaa' }}>원</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: '#EEF5FF', borderRadius: '8px', border: '0.5px solid #B5D4F4' }}>
          <span style={{ fontSize: '12px', color: '#185FA5', whiteSpace: 'nowrap' }}>추가반영액</span>
          <input style={{...inp, borderColor: '#B5D4F4'}} type="text" value={form.extra_amount} onChange={e => setForm(p=>({...p, extra_amount: e.target.value.replace(/[^0-9]/g,'')}))} placeholder="0" />
          <span style={{ fontSize: '11px', color: '#aaa' }}>원</span>
        </div>
      </div>

      {/* 재무 그리드 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        {/* 매출 & 비용 흐름 */}
        <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '12px', padding: '16px' }}>
          <div style={{ fontSize: '11px', color: '#854F0B', background: '#FAEEDA', padding: '8px 12px', borderRadius: '8px', marginBottom: '12px', lineHeight: '1.6' }}>
            ⚠️ 부가세는 현재 참고용입니다. 실제 부가세보다 더 크게 잡혀있습니다.<br/>대신, 종소세는 적게 잡혀있으므로 실제 종소세는 더 부과될 수 있습니다.
          </div>
          <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '14px' }}>📊 매출 & 비용 흐름</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {[
                { key: 'A', label: `총매출액${extraAmount>0?' (+추가반영액 '+fmt(extraAmount)+'원)':''}`, value: fmt(A), bold: false },
                { key: 'B', label: '정산예정금액', value: fmt(B), bold: false },
                { key: 'C', label: '월매입액', value: fmt(C), bold: false },
                { key: 'C+', label: '기타비용', value: fmt(Cplus), bold: false, info: true },
                { key: 'D', label: '월영업이익 (B-C-C+)', value: fmt(D), color: D>=0?'#1D9E75':'#E24B4A', bold: false },
                { key: 'E', label: '월부가세 (A×10%-C×10%)', value: fmt(E), warn: true },
                { key: '', label: '└ 판매부가세', value: fmt(vatSale), sub: true },
                { key: '', label: '└ 구입부가세', value: fmt(vatBuy), sub: true },
                { key: 'F', label: '월광고비', value: fmt(F), bold: false },
                { key: 'G', label: '세전순이익 (D-E-F)', value: fmt(G), color: G>=0?'#1D9E75':'#E24B4A', bold: false },
                { key: 'H', label: `종소세 (G×${(effRate*100).toFixed(2)}%)`, value: fmt(H), warn: true },
                { key: 'I', label: '실수령 (G-H)', value: fmt(I), color: I>=0?'#1D9E75':'#E24B4A', result: true },
              ].map((r, i) => (
                <tr key={i} style={{ borderBottom: '0.5px solid #f0f0f0', background: r.result?'#E1F5EE':r.warn?'#FAEEDA':r.info?'#E6F1FB':'transparent' }}>
                  <td style={{ padding: '6px 4px', fontSize: '10px', color: '#aaa', width: '28px' }}>{r.key}</td>
                  <td style={{ padding: '6px 4px', fontSize: r.sub?'11px':'12px', color: r.result?'#0F6E56':r.warn?'#854F0B':r.info?'#185FA5':r.sub?'#aaa':'#666', paddingLeft: r.sub?'16px':'4px' }}>{r.label}</td>
                  <td style={{ padding: '6px 4px', fontSize: '12px', fontWeight: r.result?'600':'500', textAlign: 'right', color: r.color||(r.result?'#0F6E56':r.warn?'#854F0B':r.info?'#185FA5':r.sub?'#aaa':'#1a1a1a') }}>{r.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 수익성 + 안정성 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '12px' }}>📈 수익성 지표</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {[
                { label: '영업마진율', value: `${opMarginRate.toFixed(2)}%`, sub: 'D/A×100', color: '#1a1a1a' },
                { label: '순마진율', value: `${netMarginRate.toFixed(2)}%`, sub: 'I/A×100', color: netMarginRate>=8?'#1D9E75':'#E24B4A' },
                { label: 'ROAS', value: F>0?`${roas.toLocaleString()}%`:'-', sub: 'A/F×100', color: '#534AB7' },
                { label: 'ROI', value: F>0?`${roi.toLocaleString()}%`:'-', sub: 'I/F×100', color: '#185FA5' },
              ].map((k,i) => (
                <div key={i} style={{ background: '#f9f9f9', borderRadius: '8px', padding: '10px 12px' }}>
                  <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>{k.label}</div>
                  <div style={{ fontSize: '18px', fontWeight: '500', color: k.color }}>{k.value}</div>
                  <div style={{ fontSize: '10px', color: '#aaa', marginTop: '2px' }}>{k.sub}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '12px' }}>📊 영업이익 배분 현황</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '10px' }}>
              {D > 0 ? [
                { label: '광고비', value: F, prev: prevF, color: '#185FA5', lowerIsBetter: true },
                { label: '부가세', value: E, prev: prevE, color: '#854F0B', lowerIsBetter: true },
                { label: '종소세', value: H, prev: prevH, color: '#854F0B', lowerIsBetter: true },
                { label: '실수령', value: I, prev: prevI, color: '#1D9E75', lowerIsBetter: false },
              ].map((k, i) => {
                const pct = (k.value / D * 100);
                const prevPct = (k.prev !== null && prevD > 0) ? (k.prev / prevD * 100) : null;
                const diff = prevPct !== null ? (pct - prevPct) : null;
                const isGood = diff === null ? null : (k.lowerIsBetter ? diff < 0 : diff > 0);
                const diffColor = diff === null ? '#aaa' : (isGood ? '#1D9E75' : '#E24B4A');
                const arrow = diff === null ? '' : (diff > 0 ? '↑' : diff < 0 ? '↓' : '→');
                return (
                  <div key={i} style={{ background: '#f9f9f9', borderRadius: '8px', padding: '10px 12px' }}>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>{k.label}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                      <div style={{ fontSize: '18px', fontWeight: '500', color: k.color }}>{pct.toFixed(1)}%</div>
                      {diff !== null && (
                        <div style={{ fontSize: '11px', color: diffColor, fontWeight: '500' }}>
                          {arrow}{Math.abs(diff).toFixed(1)}%p
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>{fmt(k.value)}원</div>
                  </div>
                );
              }) : <div style={{ fontSize: '12px', color: '#aaa' }}>데이터 없음</div>}
            </div>
          </div>
        </div>
      </div>

      {/* 판매처별 ROAS */}
      {data && (data.ad_smartstore > 0 || data.ad_11st > 0 || data.ad_gmarket > 0) && (
        <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>판매처별 ROAS</div>
          <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '14px' }}>ROAS = 판매처 매출 / 판매처 광고비 × 100</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.5px 1fr 0.5px 1fr', gap: '0' }}>
            {[
              { name: '스마트스토어', color: '#2a78d6', sales: data.sales_smartstore || 0, ad: data.ad_smartstore || 0, prevSales: prevData?.sales_smartstore || 0, prevAd: prevData?.ad_smartstore || 0 },
              { name: '11번가', color: '#eb6834', sales: data.sales_11st || 0, ad: data.ad_11st || 0, prevSales: prevData?.sales_11st || 0, prevAd: prevData?.ad_11st || 0 },
              { name: '지마켓', color: '#1baf7a', sales: data.sales_gmarket || 0, ad: data.ad_gmarket || 0, prevSales: prevData?.sales_gmarket || 0, prevAd: prevData?.ad_gmarket || 0 },
            ].map((p, i) => {
              const roas = p.ad > 0 ? Math.round(p.sales / p.ad * 100) : 0;
              const prevRoas = p.prevAd > 0 ? Math.round(p.prevSales / p.prevAd * 100) : 0;
              const roasDelta = prevRoas > 0 ? roas - prevRoas : null;
              const salesDelta = p.prevSales > 0 ? Math.round((p.sales - p.prevSales) / p.prevSales * 100) : null;
              const adDelta = p.prevAd > 0 ? Math.round((p.ad - p.prevAd) / p.prevAd * 100) : null;
              return (
                <React.Fragment key={p.name}>
                  {i > 0 && <div style={{ background: '#e8e8e8' }}></div>}
                  <div style={{ padding: i === 0 ? '0 14px 0 0' : i === 2 ? '0 0 0 14px' : '0 14px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '500', color: '#1a1a1a', marginBottom: '10px' }}>{p.name}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
                      <span style={{ fontSize: '20px', fontWeight: '500', color: p.color }}>{p.ad > 0 ? roas.toLocaleString() + '%' : '-'}</span>
                      {roasDelta !== null && <span style={{ fontSize: '11px', color: roasDelta >= 0 ? '#1baf7a' : '#e34948' }}>{roasDelta >= 0 ? '▲' : '▼'} {Math.abs(roasDelta).toLocaleString()}%p</span>}
                    </div>
                    {prevRoas > 0 && <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '8px' }}>전달 {prevRoas.toLocaleString()}%</div>}
                    <div style={{ borderTop: '0.5px solid #e8e8e8', paddingTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', color: '#aaa' }}>매출</span>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontSize: '11px', color: '#1a1a1a' }}>{Math.round(p.sales/10000)}만</span>
                            {salesDelta !== null && <span style={{ fontSize: '10px', color: salesDelta >= 0 ? '#1baf7a' : '#e34948' }}>{salesDelta >= 0 ? '▲' : '▼'}{Math.abs(salesDelta)}%</span>}
                          </div>
                          {p.prevSales > 0 && <span style={{ fontSize: '10px', color: '#aaa' }}>전달 {Math.round(p.prevSales/10000)}만</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', color: '#aaa' }}>광고비</span>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontSize: '11px', color: '#1a1a1a' }}>{Math.round(p.ad/10000)}만</span>
                            {adDelta !== null && <span style={{ fontSize: '10px', color: '#888' }}>{adDelta >= 0 ? '▲' : '▼'}{Math.abs(adDelta)}%</span>}
                          </div>
                          {p.prevAd > 0 && <span style={{ fontSize: '10px', color: '#aaa' }}>전달 {Math.round(p.prevAd/10000)}만</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
      {/* 연간 종합 */}
      <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <div style={{ fontSize: '13px', fontWeight: '500' }}>📅 {annualYear}년 연간 종합</div>
          <select value={annualYear} onChange={e => setAnnualYear(parseInt(e.target.value))} style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '8px', border: '0.5px solid #e8e8e8' }}>
            {[2025, NOW.getFullYear()-1, NOW.getFullYear(), NOW.getFullYear()+1].filter((y,i,a)=>a.indexOf(y)===i).sort().map(y => <option key={y} value={y}>{y}년</option>)}
          </select>
        </div>
        {annualData && (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#f9f9f9', borderBottom: '0.5px solid #e8e8e8' }}>
                  {['월','매출액','정산예정금','매입액','부가세','광고비','세전순이익'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', fontWeight: '400', color: '#888', fontSize: '11px', textAlign: h==='월'?'left':'right' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {annualData.months.map(m => (
                  <tr key={m.month} style={{ borderBottom: '0.5px solid #f0f0f0', background: (!m.has_data&&!m.is_manual)?'#fafafa':m.is_manual?'#EEF5FF':'#fff' }}>
                    <td style={{ padding: '7px 10px', color: (m.has_data||m.is_manual)?'#1a1a1a':'#aaa' }}>{m.month}월{m.is_manual&&<span style={{fontSize:'10px',color:'#185FA5',marginLeft:'4px'}}>수동</span>}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: (m.has_data||m.is_manual)?'#1a1a1a':'#aaa' }}>{(m.has_data||m.is_manual)?fmt(m.total_sales):'-'}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: (m.has_data||m.is_manual)?'#1a1a1a':'#aaa' }}>{(m.has_data||m.is_manual)?fmt(m.total_settlement):'-'}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: (m.has_data||m.is_manual)?'#1a1a1a':'#aaa' }}>{(m.has_data||m.is_manual)?fmt(m.total_purchase):'-'}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: (m.has_data||m.is_manual)?'#854F0B':'#aaa' }}>{(m.has_data||m.is_manual)?fmt(m.vat):'-'}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: (m.has_data||m.is_manual)?'#1a1a1a':'#aaa' }}>{(m.has_data||m.is_manual)?fmt(m.ad_cost):'-'}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: (m.has_data||m.is_manual)?'500':'400', color: (!m.has_data&&!m.is_manual)?'#aaa':m.pretax>=0?'#1D9E75':'#E24B4A' }}>{(m.has_data||m.is_manual)?(m.pretax>=0?'+':'')+fmt(m.pretax):'-'}</td>
                  </tr>
                ))}
                {manualData && (
                  <tr style={{ background: '#E6F1FB', borderTop: '0.5px solid #B5D4F4' }}>
                    <td style={{ padding: '8px 10px', fontSize: '11px', color: '#185FA5', fontWeight: '500' }}>수동입력</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '11px', color: '#185FA5' }}>{fmt(manualData.manual_sales)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '11px', color: '#185FA5' }}>{fmt(manualData.manual_settlement)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '11px', color: '#185FA5' }}>{fmt(manualData.manual_purchase)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '11px', color: '#185FA5' }}>
                    {fmt(Math.round(manualData.manual_sales * 0.1) - Math.round((manualData.manual_purchase + (manualData.etc_cost||0)) * 0.1))}
                  </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '11px', color: '#185FA5' }}>{fmt(manualData.ad_cost)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '11px', color: '#185FA5' }}>
                      {(() => {
                        const mp = manualData.manual_settlement - manualData.manual_purchase - manualData.etc_cost - manualData.ad_cost;
                        return (mp>=0?'+':'')+fmt(mp);
                      })()}
                    </td>
                  </tr>
                )}
                <tr style={{ background: '#f9f9f9', fontWeight: '500', borderTop: '0.5px solid #e8e8e8' }}>
                  <td style={{ padding: '8px 10px' }}>합계</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{fmt(annualData.months.reduce((s,m)=>s+m.total_sales,0))}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{fmt(annualData.months.reduce((s,m)=>s+m.total_settlement,0))}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{fmt(annualData.months.reduce((s,m)=>s+m.total_purchase,0))}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: '#854F0B' }}>{fmt(annualData.months.reduce((s,m)=>s+m.vat,0))}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{fmt(annualData.months.reduce((s,m)=>s+m.ad_cost,0))}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: annualPretax>=0?'#1D9E75':'#E24B4A' }}>{(annualPretax>=0?'+':'')+fmt(annualPretax)}</td>
                </tr>
              </tbody>
            </table>
            {manualData && manualData.memo && (
              <div style={{ margin: '10px 0', padding: '10px 14px', background: '#E6F1FB', borderRadius: '8px', fontSize: '12px', color: '#185FA5' }}>
                📝 {manualData.memo}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px', marginTop: '14px' }}>
              {[
                { label: '연 종소세', value: `${fmt(annIncomeTax)}원`, warn: true },
                { label: '지방소득세 (종소세×10%)', value: `${fmt(annLocalTax)}원`, warn: true },
                { label: '실효세율', value: `${(effRate*100).toFixed(2)}%`, warn: false },
              ].map((k,i) => (
                <div key={i} style={{ background: '#f9f9f9', borderRadius: '8px', padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: '10px', color: '#888', marginBottom: '4px' }}>{k.label}</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: k.warn?'#854F0B':'#1a1a1a' }}>{k.value}</div>
                </div>
              ))}
            </div>
            {annualPretax > 20000000 && (
              <div style={{ marginTop: '12px', padding: '10px 14px', background: '#FAEEDA', borderRadius: '8px', fontSize: '12px', color: '#854F0B' }}>
                ⚠️ 연 세전순이익이 2,000만원을 초과할 경우 지역세대주로 전환되어 건강보험료 폭탄이 발생할 수 있습니다. 절세 방법: 그 전에 법인 전환을 준비하세요.
              </div>
            )}
          </>
        )}
      </div>

      {/* 메모 */}
      <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '12px', padding: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '10px' }}>메모</div>
        <textarea value={form.memo} onChange={e => setForm(p=>({...p, memo: e.target.value}))}
          placeholder="재무 관련 메모를 입력하세요."
          style={{ width: '100%', fontSize: '12px', padding: '8px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', resize: 'vertical', minHeight: '70px', lineHeight: '1.7', boxSizing: 'border-box', color: '#1a1a1a', background: '#fff' }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
          {saveMsg && <span style={{ fontSize: '12px', color: '#1D9E75' }}>{saveMsg}</span>}
          <button onClick={handleMemoSave} disabled={saving} style={{ fontSize: '12px', padding: '6px 16px', borderRadius: '8px', border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer' }}>저장</button>
        </div>
      </div>

      {/* 다운로드 버튼 */}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
        <button onClick={openMonthlyModal} style={{ fontSize: '12px', padding: '7px 16px', borderRadius: '8px', border: '0.5px solid #378ADD', color: '#378ADD', background: '#fff', cursor: 'pointer' }}>월별 데이터 다운로드</button>
        <button onClick={openAnnualModal} style={{ fontSize: '12px', padding: '7px 16px', borderRadius: '8px', border: '0.5px solid #7F77DD', color: '#7F77DD', background: '#fff', cursor: 'pointer' }}>연간 데이터 다운로드</button>
      </div>

      {/* 월별 다운로드 모달 */}
      {showMonthlyModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '320px', maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px' }}>월별 데이터 다운로드</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
              <button onClick={() => setSelectedMonths(selectedMonths.length === availableMonths.length ? [] : [...availableMonths])}
                style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '6px', border: '0.5px solid #e8e8e8', background: '#fff', cursor: 'pointer', color: '#888' }}>
                {selectedMonths.length === availableMonths.length ? '전체해제' : '전체선택'}
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', border: '0.5px solid #e8e8e8', borderRadius: '8px', marginBottom: '16px' }}>
              {availableMonths.map(m => {
                const [y, mo] = m.split('-');
                return (
                  <div key={m} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0', cursor: 'pointer' }}
                    onClick={() => setSelectedMonths(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m])}>
                    <input type="checkbox" checked={selectedMonths.includes(m)} onChange={() => {}} style={{ cursor: 'pointer' }} />
                    <span style={{ fontSize: '13px' }}>{y}년 {parseInt(mo)}월</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setShowMonthlyModal(false)} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff', fontSize: '13px', cursor: 'pointer', color: '#888' }}>취소</button>
              <button onClick={downloadMonthly} disabled={downloading} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: 'none', background: '#378ADD', color: '#fff', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                {downloading ? '다운로드 중...' : `다운로드 (${selectedMonths.length}개월)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 연간 다운로드 모달 */}
      {showAnnualModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '280px' }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px' }}>연간 데이터 다운로드</div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>연도 선택</div>
              <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)}
                style={{ width: '100%', fontSize: '13px', padding: '8px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff' }}>
                {availableYears.map(y => <option key={y} value={y}>{y}년</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setShowAnnualModal(false)} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff', fontSize: '13px', cursor: 'pointer', color: '#888' }}>취소</button>
              <button onClick={downloadAnnual} disabled={downloading} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: 'none', background: '#7F77DD', color: '#fff', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                {downloading ? '다운로드 중...' : '다운로드'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
